import React, { FC } from 'react'

import { useDispatch } from 'react-redux'
import { useParams } from 'react-router'
import { styled } from 'styled-components'

import { fetchIncognitoRequestStatusUniNoticeAction } from 'actions/profile/fetchIncognitoRequestStatusUniNotice'
import { fetchRequestIncognitoStatusAction } from 'actions/profile/fetchRequestIncognitoStatusAction'
import { fetchUniNoticeAction } from 'actions/uninotice/uniNoticeAction'
import { ModerationStatus } from 'api/photo/photo.types'
import { PhotoStatusOverflowAlbums } from 'common/constants'
import {
  Incognito,
  PhotoVerified,
  DefaultPhoto,
  PhotoViewerPhotoStatus,
} from 'components/page/PhotoViewer/GroupTopIcon'
import { PhotoViewerParams } from 'components/page/PhotoViewer/PhotoViewer.paths'
import { TooltipMenu } from 'components/page/PhotoViewer/TooltipMenu/TooltipMenu'
import { incognitoCurrentUserEnabledNoticeId } from 'components/uninotice/uniNoticeIdList'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'

interface PhotoViewerIconsAndTooltipProps {
  selectedPhotoIndex: number
}

export const PhotoViewerIconsAndTooltip: FC<
  PhotoViewerIconsAndTooltipProps
> = ({ selectedPhotoIndex }) => {
  const dispatch = useDispatch()
  const { userId } = useParams<PhotoViewerParams>()

  const {
    currentUserId,
    photos,
    isIncognitoStatus,
    isCurrentUserCanSeePhotos,
  } = useShallowEqualSelector(
    ({
      userReducer: { userId: currentUserId },
      photoViewerReducer: {
        photos,
        isIncognitoStatus,
        isCurrentUserCanSeePhotos,
      },
    }) => ({
      currentUserId,
      photos,
      isIncognitoStatus,
      isCurrentUserCanSeePhotos,
    })
  )

  const showIncognitoIcon = isIncognitoStatus && !isCurrentUserCanSeePhotos

  const isCurrentUser = currentUserId === Number(userId)

  const selectedPhoto = photos[selectedPhotoIndex]

  const {
    moderationStatus,
    pending,
    verified: profilePhotosVerified,
    main: isDefault,
  } = selectedPhoto

  const handleClickIncognito = async () => {
    if (isCurrentUser) {
      dispatch(fetchUniNoticeAction(incognitoCurrentUserEnabledNoticeId))
    } else {
      await dispatch(fetchRequestIncognitoStatusAction(userId))
      await dispatch(fetchIncognitoRequestStatusUniNoticeAction(userId))
    }
  }

  const incognitoElement = (
    <Incognito
      isCurrentUserIncognito={showIncognitoIcon}
      onClick={handleClickIncognito}
    />
  )

  if (isCurrentUser) {
    if (
      moderationStatus === ModerationStatus.rejected ||
      (!pending && moderationStatus === ModerationStatus.pending)
    ) {
      return (
        <PhotoStatusPanel>
          <PhotoViewerPhotoStatus
            status={moderationStatus}
            isFromPhotoViewer={true}
          />
        </PhotoStatusPanel>
      )
    }

    if (pending) {
      return (
        <PhotoStatusPanel>
          <PhotoViewerPhotoStatus
            status={PhotoStatusOverflowAlbums}
            isFromPhotoViewer={true}
          />
        </PhotoStatusPanel>
      )
    }

    return (
      <PhotoStatusPanel>
        <PhotoVerified isVerified={profilePhotosVerified} />
        <DefaultPhoto isDefaultPhoto={isDefault} />
        {incognitoElement}
        <TooltipMenu selectedPhotoIndex={selectedPhotoIndex} />
      </PhotoStatusPanel>
    )
  }

  return (
    <PhotoStatusPanel>
      {incognitoElement}
      <PhotoVerified isVerified={profilePhotosVerified} />
      <TooltipMenu selectedPhotoIndex={selectedPhotoIndex} />
    </PhotoStatusPanel>
  )
}

export const PhotoStatusPanel = styled.div<{ $padding?: number }>`
  display: flex;
  position: absolute;
  top: 0;
  ${(props) => props.theme.right}: 0;

  z-index: 1;
  align-items: center;
  cursor: pointer;
  padding: ${(props) => props.$padding}px;
  ${(props) => props.theme.marginRight}: 8px;

  & > * {
    margin: 0;
  }

  & > * + * {
    width: unset;
    ${(props) => props.theme.marginLeft}: 10px;
    ${(props) => props.theme.marginRight}: 0;
  }
`

PhotoStatusPanel.defaultProps = {
  $padding: 0,
}
